import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

export type ChipValue = string | number | boolean | null;

export interface ChipOption {
  label: string;
  value: ChipValue;
  disabled?: boolean;
}

interface ChipViewOption extends ChipOption {
  selected: boolean;
}

@Component({
  selector: 'app-chips-selector',
  imports: [],
  templateUrl: './chips-selector.component.html',
  styleUrls: ['./chips-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChipsSelectorComponent {
  @Input() set options(options: ChipOption[]) {
    this._options = options ?? [];
    this.syncViewOptions();
  }
  @Input() set selectionMode(mode: 'single' | 'multiple') {
    this._selectionMode = mode;
    this.selectedValues = new Set();
    this.syncViewOptions();
  }

  @Input() set value(value: ChipValue | ChipValue[] | null) {
    this._value = value;
    this.syncSelectedValues();
    this.syncViewOptions();
  }

  @Output() valueChange = new EventEmitter<ChipValue | ChipValue[]>();

  viewOptions: ChipViewOption[] = [];
  private selectedValues = new Set<ChipValue>();
  private _options: ChipOption[] = [];
  private _selectionMode: 'single' | 'multiple' = 'single';
  private _value: ChipValue | ChipValue[] | null = null;

  toggle(option: ChipOption): void {
    if (option.disabled) {
      return;
    }

    if (this._selectionMode === 'single') {
      const nextValue = this._value === option.value ? null : option.value;
      this.value = nextValue;
      this.valueChange.emit(nextValue);
      return;
    }

    const current = Array.isArray(this._value) ? [...this._value] : [];
    const index = current.findIndex((item) => item === option.value);

    if (index >= 0) {
      current.splice(index, 1);
    } else {
      current.push(option.value);
    }

    this.value = current;
    this.valueChange.emit(current);
  }

  private syncSelectedValues(): void {
    if (this._selectionMode === 'multiple' && Array.isArray(this._value)) {
      this.selectedValues = new Set(this._value);
      return;
    }

    if (this._selectionMode === 'single' && !Array.isArray(this._value)) {
      this.selectedValues = new Set([this._value]);
      return;
    }

    this.selectedValues = new Set();
  }

  private syncViewOptions(): void {
    this.viewOptions = this._options.map((option) => ({
      ...option,
      selected: this.selectedValues.has(option.value),
    }));
  }
}
